import { useEffect, useRef } from 'react';

import { SiteConfigManager } from '@atlas/core';

import {
  CHAT_READY_TIMEOUT_MS,
  classifyLiveChatDeepLink,
  DEEP_LINK_SESSION_KEY,
  removeLiveChatParam,
  waitForChatReady,
} from 'components/LiveChat/liveChatDeepLink';
import useLiveChat from 'components/LiveChat/useLiveChat';

/**
 * Read the session guard flag, falling back gracefully when `sessionStorage`
 * is unavailable (e.g. privacy mode). Never throws.
 */
const readSessionGuard = () => {
  try {
    return window.sessionStorage.getItem(DEEP_LINK_SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Set the session guard flag. Best-effort: any storage error is swallowed so
 * the in-mount ref guard remains the source of truth for the current load.
 */
const writeSessionGuard = () => {
  try {
    window.sessionStorage.setItem(DEEP_LINK_SESSION_KEY, '1');
  } catch {
    // Ignore storage errors: the in-mount ref guard still prevents re-open.
  }
};

/**
 * Strip the reserved deep-link parameter from the address bar via
 * `history.replaceState`, preserving pathname + hash and updating the URL
 * without triggering a page reload. Best-effort and never throws.
 */
const stripDeepLinkParam = () => {
  try {
    const { hash, pathname, search } = window.location;
    const nextSearch = removeLiveChatParam(search);
    const searchSuffix = nextSearch ? `?${nextSearch}` : '';
    const nextUrl = `${pathname}${searchSuffix}${hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  } catch {
    // Ignore history errors: param removal is best-effort, chat already opened.
  }
};

/**
 * Orchestration hook for the LiveChat deep-link feature.
 *
 * On mount (and only once per session), it inspects `window.location.search`
 * for the reserved deep-link parameter. When present and chat is available and
 * configured, it waits for the provider to reach the ready state (bounded by a
 * 30s budget), issues a single deterministic `openChat()`, and removes the
 * parameter from the URL. It performs no viewport checks and uses no router
 * hooks (the provider is mounted outside the Router), relying solely on
 * `window.location` / `window.history`. All provider, history, and storage
 * interactions are wrapped so no chat-related error is surfaced to the client.
 */
const useLiveChatDeepLink = () => {
  const { openChat } = useLiveChat();
  const hasHandledRef = useRef(false);

  useEffect(() => {
    if (!classifyLiveChatDeepLink(window.location.search)) {
      return undefined;
    }

    if (hasHandledRef.current || readSessionGuard()) {
      return undefined;
    }

    const isChatAvailable = SiteConfigManager.getConfig(
      'config.config.support.live_chat.mobile.is_available',
    );
    const chatType = SiteConfigManager.getConfig('config.config.support.live_chat.mobile.type');

    if (!isChatAvailable || !chatType) {
      return undefined;
    }

    let removalTimer = null;

    const cleanupReady = waitForChatReady(chatType, {
      onReady: () => {
        hasHandledRef.current = true;
        writeSessionGuard();

        try {
          openChat();
        } catch {
          // Provider open failures must never surface a chat error.
        }

        removalTimer = setTimeout(stripDeepLinkParam, 0);
      },
      onTimeout: () => {
        // Not ready within the budget: leave the widget collapsed, no error.
      },
      timeoutMs: CHAT_READY_TIMEOUT_MS,
    });

    return () => {
      cleanupReady();
      if (removalTimer !== null) {
        clearTimeout(removalTimer);
        removalTimer = null;
      }
    };
    // Mount-once orchestration: intentionally empty dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useLiveChatDeepLink;
